<template>
  <div name="organism-ipam-management_container">
    <div class="ipam-management-card">
      <!-- ? Header Section Start -->
      <section name="ipam-header" class="card-padding-top card-padding-x">
        <span class="card__header--topic">IP Address Management</span>
      </section>
      <!-- ? Header Section End -->

      <!-- ? Filter Section Start -->
      <section name="ipam-filter" class="card-padding-x">
        <div class="row mt-2">
          <!-- * Filter Subnet Start-->
          <div class="col-12 col-md-8">
            <atom-multi-select
              v-model="filterData.subnetAddress"
              title="Subnet"
              placeholder="Select Subnet"
              label="subnetAddress"
              :isClearable="false"
              :isAppendToBody="false"
              :isEnableProper="false"
              :option="subnetAddressOption"
            ></atom-multi-select>
          </div>
          <!-- * Filter Subnet End-->
          <div class="col-12 col-md-4 mt-1 mt-md-0">
            <atom-input
              v-model="filterData.idcId"
              :title="contractIdLabel"
              type="number"
              :placeholder="contractIdLabel"
              :maxLength="8"
              @onKeyupEnter="onClickButtonSearch"
            ></atom-input>
          </div>

          <!-- * Filter Status Start-->
          <div class="col-12 col-md-4 mt-1">
            <atom-multi-select
              v-model="filterData.subnetStatus"
              title="Status"
              placeholder="Select Status"
              label="statusName"
              :isEnableProper="false"
              :isAppendToBody="false"
              :option="ipAddressStatusOption"
            ></atom-multi-select>
          </div>
          <!-- * Filter Status End-->

          <!-- * Filter Tag Start-->
          <div class="col-12 col-md-4 mt-1">
            <atom-multi-select
              v-model="filterData.subnetTags"
              title="Subnet Tag"
              placeholder="Select Subnet Tags"
              :isEnableProper="false"
              :isAppendToBody="false"
              :option="subnetAndIpTagsOption"
            ></atom-multi-select>
          </div>

          <div class="col-12 col-md-4 mt-1">
            <atom-multi-select
              v-model="filterData.ipTags"
              title="IP Address Tag"
              placeholder="Select Address Tags"
              :isEnableProper="false"
              :isAppendToBody="false"
              :option="subnetAndIpTagsOption"
            ></atom-multi-select>
          </div>
          <!-- * Filter Tag End-->

          <!-- Filter IP Version Start -->
          <div class="col-12 col-md-3 mt-1">
            <atom-select
              v-model="filterData.ipVersion"
              title="IP Version"
              placeholder="Select IP Version"
              :isAppendToBody="false"
              :isEnableProper="false"
              :option="ipAddressTypeOptions"
              label="topic"
            ></atom-select>
          </div>
          <!-- Filter IP Version End -->

          <!-- * Filter IP Address/Description Start -->
          <div class="col-12 col-md-9 mt-1">
            <atom-input
              v-model="filterData.subnetIpAddressOrDescription"
              title="Filter"
              placeholder="Filter IP Address, Organization Name or IP Description"
              @onKeyupEnter="onClickButtonSearch"
            ></atom-input>
          </div>
          <!-- * Filter IP Address/Description End -->
        </div>

        <!-- * Filter Allocation datetime Start-->
        <div class="row">
          <div class="col-12 col-md-4 col-lg-3 mt-1">
            <atom-datepicker
              datepickerId="molecule-date-picker-period--start"
              title="Allocation Start Date"
              v-model="filterData.allocationDateTime.start"
              :maxDate="
                filterData.allocationDateTime.end
                  ? filterData.allocationDateTime.end
                  : filterData.allocationDateTime.now
              "
              type="dateNtime"
              placeholder="Select Start Date"
              class="molecule-date-picker-period--start"
              :isDisabled="!filterData.isEnableAllocationDateTime"
            ></atom-datepicker>
          </div>
          <div class="col-12 col-md-4 col-lg-3 mt-1">
            <atom-datepicker
              datepickerId="molecule-date-picker-period--en"
              title="Allocation End Date"
              v-model="filterData.allocationDateTime.end"
              :minDate="filterData.allocationDateTime.start"
              :maxDate="filterData.allocationDateTime.now"
              type="dateNtime"
              placeholder="Select End Date"
              class="molecule-date-picker-period--end"
              :isDisabled="!filterData.isEnableAllocationDateTime"
            ></atom-datepicker>
          </div>
          <div
            class="col-12 col-md-4 mt-1 d-flex justify-content-end justify-content-md-start"
          >
            <div class="d-flex flex-column">
              <label>Filter Date?</label>
              <atom-switch
                v-model="filterData.isEnableAllocationDateTime"
                class="filter__switch"
              ></atom-switch>
            </div>
            <div class="d-flex align-items-end">
              <atom-button
                buttonId="button-search"
                iconLeft="SearchIcon"
                class="search__button ml-2"
                @click="onClickButtonSearch"
              ></atom-button>
              <div
                class="reset-filter-button ml-1"
                @click="clickButtonClearFilter"
              >
                <span>Reset</span>
              </div>
            </div>
          </div>
          <!-- * Filter Allocation datetime End-->
        </div>
      </section>
      <!-- ? Filter Section End -->

      <hr />

      <!-- ? IPAM Select And IP Address Data Start -->
      <section name="ipam-select-and-data">
        <div class="row mx-0 custom-max-height">
          <!-- * Sidebar Menu Start -->
          <div
            class="col-12 col-lg-3 px-0 pb-2 pb-lg-0 subnet-sidebar position-relative"
          >
            <div class="row mx-0 custom-position-sticky">
              <!-- * Button Wrapper Start -->
              <div
                v-if="hasUpdateIpamPermission"
                class="col-12 px-0 py-2 d-flex justify-content-center align-items-center"
              >
                <atom-button
                  buttonId="button-add-subnet"
                  @click="openModal('modal-create-new-subnet')"
                  class="custom-button-width-90"
                >
                  <feather-icon icon="PlusIcon" size="16" />
                  <span> Add New</span>
                </atom-button>
              </div>
              <!-- * Button Wrapper End -->

              <!-- * Select Subnet Start -->
              <div class="col-12 px-0">
                <atom-collapse
                  v-for="(sidebar, index) in subnetAddressForSidebarOption"
                  :key="index"
                  :isVisible="index === 0"
                >
                  <template #header>
                    {{ sidebar.siteName || "Site not found" }}
                  </template>
                  <template>
                    <div
                      v-if="sidebar.subnets.length"
                      class="subnet-card--custom-max-height"
                    >
                      <molecule-subnet-card
                        v-for="subnet in sidebar.subnets"
                        :key="subnet.subnetId"
                        :subnetData="subnet"
                        :selectedSubnetIdx="selectedSubnetIdx"
                        :hasManageIpAddressPermission="
                          hasUpdateIpamPermission
                        "
                        :hasDeletePermission="hasDeleteIpamPermission"
                        @onSelectSubnetFromSidebar="onSelectSubnetFromSidebar"
                        @onRequestEditSubnet="onRequestEditSubnet"
                        @onRequestDeleteSubnet="onRequestDeleteSubnet"
                      ></molecule-subnet-card>
                    </div>
                    <div v-else class="d-flex justify-content-center pb-2">
                      <span>No subnet available, Please add first.</span>
                    </div>
                  </template>
                </atom-collapse>
              </div>
              <!-- * Select Subnet End -->
            </div>
          </div>
          <!-- * Sidebar Menu End -->

          <!-- * Table Start -->
          <div class="col-12 col-lg-9 px-0 position-relative">
            <div v-if="selectedSubnetIdx" class="row ip-header-sticky">
              <div class="col-12 px-0">
                <div
                  class="d-md-flex justify-content-between align-items-center"
                  :class="[
                    selectedSubnetIpVersion === 6 &&
                    hasUpdateIpamPermission
                      ? 'card-padding-x'
                      : 'card-padding',
                  ]"
                >
                  <div class="d-flex align-items-center">
                    <span class="text-weight-medium text-size-18">
                      {{ selectedSubnet }}
                    </span>
                    <b-badge
                      v-if="hasUpdateIpamPermission"
                      variant="light-primary"
                      class="ml-1"
                    >
                      {{ checkedIpAddressList.length }}/{{
                        tableIpAddressItems.length
                      }}
                      IP Selected</b-badge
                    >
                    <b-badge v-else variant="light-primary" class="ml-1">
                      {{ tableIpAddressItems.length }}
                      IP Addresses</b-badge
                    >
                  </div>
                  <div
                    v-if="isShowManageIpAddressButton"
                    class="m-1 d-flex justify-content-end"
                  >
                    <atom-button
                      buttonId="button-add-subnet"
                      @click="clickAddNewIpV6"
                      buttonStyle="border"
                    >
                      <span> Add New IPV6 </span>
                    </atom-button>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 px-0">
              <div v-if="selectedSubnetIdx">
                <b-table
                  sticky-header
                  responsive
                  :items="tableIpAddressItems"
                  :fields="tableIpAddressFields"
                  :hover="hasUpdateIpamPermission"
                  @row-clicked="selectRow"
                >
                  <template #head(isChecked)>
                    <div v-if="hasUpdateIpamPermission" class="pb-1">
                      <b-form-checkbox
                        v-model="checkAllIpAddress"
                        plain
                        class="vs-checkbox-con"
                        @change="toggleAll"
                      >
                        <span class="vs-checkbox">
                          <span class="vs-checkbox--check">
                            <i class="vs-icon feather icon-check" />
                          </span>
                        </span>
                      </b-form-checkbox>
                    </div>
                  </template>
                  <template #cell(isChecked)="data">
                    <div
                      v-if="hasUpdateIpamPermission"
                      class="custom-cell-checkbox d-flex"
                      @click.self="doNothing()"
                    >
                      <b-form-checkbox
                        v-model="data.item.isChecked"
                        plain
                        class="vs-checkbox-con"
                      >
                        <span class="vs-checkbox">
                          <span class="vs-checkbox--check">
                            <i class="vs-icon feather icon-check" />
                          </span>
                        </span>
                      </b-form-checkbox>
                      <b-dropdown
                        v-if="data.item.isChecked"
                        no-caret
                        right
                        v-ripple.400="'rgba(30, 30, 30, 0.15)'"
                        variant="flat-dark"
                        size="sm"
                        class="edit__dropdown"
                      >
                        <template #button-content>
                          <div>
                            <feather-icon
                              icon="MoreVerticalIcon"
                              class="custom-padding-bottom"
                            ></feather-icon>
                          </div>
                        </template>
                        <b-dropdown-item
                          @click="clickEditMultipleIpAddressFromTripleDot"
                        >
                          <div class="d-flex align-items-center">
                            <feather-icon
                              icon="SettingsIcon"
                              class="mr-1"
                            ></feather-icon>
                            <span>Edit IP Addresses</span>
                          </div>
                        </b-dropdown-item>
                        <b-dropdown-item
                          v-if="selectedSubnetIpVersion === 6"
                          @click="clickDeleteMultipleIpAddressFromTripleDot"
                        >
                          <div class="d-flex align-items-center">
                            <feather-icon
                              icon="Trash2Icon"
                              class="mr-1"
                            ></feather-icon>
                            <span>Delete IP Addresses</span>
                          </div>
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                  </template>
                  <template #cell(ip)="data">
                    <div class="custom-cell-subnet-ip-address">
                      {{ data.value.ip }}
                    </div>
                    <div class="custom-cell-subnet-ip-status">
                      <b-badge
                        pill
                        :variant="badgeVariant(data.value.status)"
                        class="badge--width"
                        >{{ toPascalCase(data.value.status) }}</b-badge
                      >
                    </div>
                  </template>
                  <template #cell(tags)="data">
                    <div class="custom-cell-subnet-tags">
                      <b-badge
                        v-for="(tag, index) in data.item.tags"
                        :key="index"
                        variant="light-primary"
                        class="badge--custom-margin-right"
                        >{{ tag }}</b-badge
                      >
                    </div>
                  </template>
                  <template #cell(idcId)="data">
                    <div class="custom-cell-subnet-idc-id">
                      {{
                        data.value
                          ? data.value.toString().padStart(8, "0")
                          : "-"
                      }}
                    </div>
                    <div class="sub-header-2">
                      {{ getOrganizationName(data.value) }}
                    </div>
                  </template>
                  <template #cell(ipDescription)="data">
                    <div class="custom-cell-subnet-description">
                      {{ data.value || "-" }}
                    </div>
                  </template>
                  <template #cell(allocateDate)="data">
                    <div
                      class="custom-cell-allocate-date"
                      :class="{ 'text-right': data.value.date }"
                    >
                      <div>{{ data.value.date || "-" }}</div>
                      <div class="sub-header text-right">
                        {{ data.value.time }}
                      </div>
                    </div>
                  </template>
                  <template
                    v-if="isShowManageIpAddressButton"
                    #cell(deleteIcon)="data"
                  >
                    <atom-button
                      buttonId="button-delete"
                      iconLeft="Trash2Icon"
                      buttonStyle="flat"
                      buttonColor="secondary"
                      :isDisable="
                        data.item.ip.status !== 'AVAILABLE' || !!data.item.idcId
                      "
                      @click="clickDeleteSingleIp(data.item)"
                    ></atom-button>
                  </template>
                </b-table>
              </div>
              <div v-else>
                <div
                  class="d-flex flex-column justify-content-center align-items-center"
                >
                  <feather-icon icon="InfoIcon" size="50" class="mt-5" />
                  <span class="mt-1 mb-3">Please select subnet</span>
                </div>
              </div>
            </div>
          </div>
          <!-- * Table End -->
        </div>
      </section>
      <!-- ? IPAM Select And IP Address Data End -->
    </div>
    <footer style="height: 28px"></footer>

    <!-- ? Start Modal Section -->
    <!-- * Modal Create New Subnet Start -->
    <molecule-modal-dialog
      modalId="modal-create-new-subnet"
      buttonTextLeft="Create Subnet"
      buttonColorLeft="primary"
      textTitle="New Subnet"
      :isSlotBody="true"
      :isCloseOnBackDrop="false"
      :isDisableButtonLeft="isErrorOccurAtNewSubnetData"
      @clickButtonLeft="onClickConfirmCreateNewSubnetFromModal"
      @clickButtonRight="onClickCancelCreateNewSubnetFromModal"
      @clickIconClose="onClickCancelCreateNewSubnetFromModal"
    >
      <template #body>
        <div class="modal-content-container custom-max-height">
          <div class="row mt-2 mb-1">
            <div class="col-12">
              <atom-select
                v-model="newSubnetData.site"
                title="Site"
                placeholder="Select Site"
                :isAsterisk="true"
                :isError="isErrorAtNewSubnetSite"
                errorMessage="Please Select Site"
                :isClearable="false"
                :isAppendToBody="false"
                :isEnableProper="false"
                :option="siteOptions"
                label="text"
              ></atom-select>
            </div>
          </div>
          <div class="row mb-1">
            <div class="col-8 mb-1">
              <div class="text__title">
                IP Address Type<label class="text__asterisk">*</label>
              </div>
              <div class="d-flex">
                <molecule-radio-select-data
                  v-for="(option, index) in ipAddressTypeOptions"
                  :key="index"
                  :radioOption="option"
                  :selectedRadio="newSubnetData.type"
                  @selected="newSubnetData.type = $event"
                >
                </molecule-radio-select-data>
              </div>
            </div>
            <div class="col-8 flex-row">
              <atom-input
                v-model="newSubnetData.subnetAddress"
                title="Subnet"
                placeholder="100.0.0.0"
                :isAsterisk="true"
                :isError="isErrorAtNewSubnetAddress"
                :errorMessage="errorMessageAtInput"
              ></atom-input>
            </div>
            <div class="col-4 flex-row">
              <atom-select
                v-model="newSubnetData.subnetMask"
                title="Mask"
                placeholder="24"
                :isDisable="!newSubnetData.type"
                :isError="isErrorAtNewSubnetMask"
                :isClearable="false"
                :isAppendToBody="false"
                :isEnableProper="false"
                :option="
                  newSubnetData.type
                    ? newSubnetData.type.value === 4
                      ? subnetMaskOptionIpV4
                      : subnetMaskOptionIpV6
                    : []
                "
              ></atom-select>
            </div>
          </div>
          <div class="row mb-1">
            <div class="col-12">
              <atom-text-area
                v-model="newSubnetData.subnetDescription"
                :isAsterisk="true"
                :isError="isErrorAtNewSubnetDescription"
                :errorMessage="errorMessageAtInput"
                title="Description"
                placeholder="Description"
              ></atom-text-area>
            </div>
          </div>
          <div class="row mb-1">
            <div class="col-12">
              <atom-multi-select
                v-model="newSubnetData.subnetTags"
                title="Tags"
                placeholder="Select Tags"
                :isAsterisk="true"
                :isPushTaggable="true"
                :isError="isErrorAtNewSubnetTags"
                :errorMessage="errorMessageAtSelector"
                :option="subnetAndIpTagsOption"
              ></atom-multi-select>
            </div>
          </div>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- * Modal Create New Subnet End -->

    <!-- * Modal Edit Subnet Start -->
    <molecule-modal-dialog
      modalId="modal-edit-subnet"
      buttonTextLeft="Save Changes"
      buttonColorLeft="primary"
      textTitle="Subnet Detail"
      :isDisableButtonLeft="!isTargetSubnetChange || isErrorOccurAtTargetSubnet"
      :isSlotBody="true"
      :isCloseOnBackDrop="false"
      @clickButtonLeft="onClickConfirmEditSubnetFromModal"
      @clickButtonRight="onClickCancelEditSubnetFromModal"
      @clickIconClose="onClickCancelEditSubnetFromModal"
    >
      <template #body>
        <div class="modal-content-container custom-overflow-y">
          <div class="row mt-2 mb-1">
            <div class="col-12">
              <atom-select
                v-model="targetSubnet.site"
                title="Site"
                placeholder="Select Site"
                :isAsterisk="true"
                :isError="isErrorAtTargetSubnetSite"
                errorMessage="Please Select Site"
                :isClearable="false"
                :isAppendToBody="false"
                :isEnableProper="false"
                :option="siteOptions"
                label="siteName"
                :isDisable="!!targetSubnet.site"
              ></atom-select>
            </div>
          </div>
          <div class="row mb-1">
            <div class="flex-row col-8">
              <atom-input
                v-model="targetSubnet.subnetAddress"
                title="Subnet"
                :isDisable="true"
                :isAsterisk="true"
              ></atom-input>
            </div>
            <div class="col-4 flex-row">
              <atom-select
                v-model="targetSubnet.subnetMask"
                title="Mask"
                :isDisable="true"
                :isAsterisk="true"
                :isClearable="false"
                :option="
                  selectedSubnetIpVersion === 4
                    ? subnetMaskOptionIpV4
                    : subnetMaskOptionIpV6
                "
              ></atom-select>
            </div>
          </div>
          <div class="row mb-1">
            <div class="col-12">
              <atom-text-area
                v-model="targetSubnet.subnetDescription"
                :isAsterisk="true"
                :isError="isErrorAtTargetSubnetDescription"
                :errorMessage="errorMessageAtInput"
                title="Description"
                placeholder="Description"
              ></atom-text-area>
            </div>
          </div>
          <div class="row mb-1">
            <div class="col-12">
              <atom-multi-select
                v-model="targetSubnet.subnetTags"
                title="Tags"
                placeholder="Select Tags"
                :isAsterisk="true"
                :isPushTaggable="true"
                :isError="isErrorAtTargetSubnetTags"
                :errorMessage="errorMessageAtSelector"
                :option="subnetAndIpTagsOption"
              ></atom-multi-select>
            </div>
            <div class="col-12 checkbox--custom-margin-top">
              <atom-checkbox
                v-model="targetSubnet.isOverrideTagsToIp"
                title="Override tags to all ip address under the subnet"
                :idx="targetSubnet.subnetId"
                :isColorTextOnActive="true"
              ></atom-checkbox>
            </div>
          </div>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- * Modal Edit Subnet End -->

    <!-- * Modal Confirm Delete Subnet Start -->
    <molecule-modal-dialog
      modalId="modal-confirm-delete-subnet"
      icon="AlertCircleIcon"
      :isTextCenter="true"
      :textTitle="`Do you want to delete\nsubnet ${targetSubnet.subnetAddress} / ${targetSubnet.subnetMask}`"
      textSubtitle="You won’t be able to revert this!"
      buttonTextLeft="Delete"
      buttonTextRight="Cancel"
      buttonColorLeft="danger"
      :isCloseOnBackDrop="false"
      @clickButtonLeft="onClickConfirmDeleteSubnetFromModal"
      @clickButtonRight="onClickCancelDeleteSubnetFromModal"
      @clickIconClose="onClickCancelDeleteSubnetFromModal"
    >
    </molecule-modal-dialog>
    <!-- * Modal Confirm Delete Subnet End -->

    <!-- * Modal Confirm Edit Ip Address Start -->
    <molecule-modal-dialog
      modalId="modal-edit-ip-address"
      buttonTextLeft="Save Changes"
      buttonColorLeft="primary"
      textTitle="IP Address Detail"
      :isDisableButtonLeft="
        !isTargetIpAddressChange || isErrorOccurAtTargetIpAddress
      "
      :isSlotBody="true"
      :isCloseOnBackDrop="false"
      @clickButtonLeft="onClickConfirmEditIpAddressFromModal"
      @clickButtonRight="onClickCancelEditIpAddressFromModal"
      @clickIconClose="onClickCancelEditIpAddressFromModal"
    >
      <template #body>
        <div class="modal-content-container custom-overflow-y">
          <div class="row mt-2 mb-1">
            <div class="col-12">
              <atom-input
                v-model="targetIpAddress.ip"
                title="IP Address"
                placeholder="100.0.0.0"
                :isDisable="true"
                :isAsterisk="true"
              ></atom-input>
            </div>
            <div class="col-12 mt-1">
              <atom-text-area
                v-model="targetIpAddress.ipDescription"
                title="Description"
                row="3"
              ></atom-text-area>
            </div>
            <div class="col-12 mt-1">
              <atom-select
                v-model="targetIpAddress.ipStatus"
                title="Status"
                placeholder="Select IP Status"
                :isAsterisk="true"
                :isClearable="false"
                :isError="isErrorAtTargetIpAddressStatus"
                :errorMessage="errorMessageAtSelector"
                :isAppendToBody="false"
                :isEnableProper="false"
                :option="ipAddressStatusOption"
              ></atom-select>
            </div>
            <div class="col-12 mt-1">
              <atom-multi-select
                v-model="targetIpAddress.tags"
                title="Tags"
                placeholder="Select Tags"
                :isAsterisk="true"
                :isPushTaggable="true"
                :isError="isErrorAtTargetIpAddressTags"
                :errorMessage="errorMessageAtSelector"
                :option="subnetAndIpTagsOption"
              ></atom-multi-select>
            </div>
            <div class="col-12 mt-1">
              <atom-datepicker
                v-model="targetIpAddress.allocationDateTime"
                title="Allocate Date"
                datepickerId="molecule-date-picker-allocate-date"
                type="dateNtime"
                placeholder="Select Start Date"
                :isClearable="true"
                class="molecule-date-picker-period-allocate-date"
              ></atom-datepicker>
            </div>
            <div class="col-12 mt-1">
              <atom-select
                v-model="targetIpAddress.idcId"
                :title="contractIdLabel"
                :placeholder="`Select ${contractIdLabel}`"
                label="idcId"
                :option="customerIdcIdList"
              ></atom-select>
            </div>
            <div v-if="targetIpAddress.idcId" class="col-12 mt-1">
              <label class="text__name-key">Organization Name: </label>
              <label class="ml-1 text__name-value">{{
                targetIpAddress.idcId.name
              }}</label>
            </div>
          </div>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- * Modal Edit Ip Address End -->

    <!-- * Modal Confirm Edit Multiple Ip Address Start -->
    <molecule-modal-dialog
      modalId="modal-edit-multiple-ip-address"
      buttonTextLeft="Save Changes"
      buttonColorLeft="primary"
      textTitle="IP Address Detail"
      textSubtitle="The detail will override all the old data"
      :isDisableButtonLeft="
        !isTargetMultipleIpAddressChange ||
        isErrorOccurAtTargetMultipleIpAddress
      "
      :isSlotBody="true"
      :isCloseOnBackDrop="false"
      @clickButtonLeft="onClickConfirmEditMultipleIpAddressFromModal"
      @clickButtonRight="onClickCancelEditMultipleIpAddressFromModal"
      @clickIconClose="onClickCancelEditMultipleIpAddressFromModal"
    >
      <template #body>
        <div class="modal-content-container custom-overflow-y">
          <div class="row mt-2 mb-1">
            <div class="col-12">
              <span><b>IP Address List</b></span>
              <div
                class="preview-multiple-ip-box custom-edition-box-height mt-1"
              >
                <ul>
                  <li
                    v-for="(ipAddress, index) in targetMultipleIpAddress.ipList"
                    :key="index"
                  >
                    {{ ipAddress.ip }}
                  </li>
                </ul>
              </div>
            </div>
            <div class="col-12 mt-1">
              <atom-text-area
                v-model="targetMultipleIpAddress.ipDescription"
                title="Description"
                row="3"
              ></atom-text-area>
            </div>
            <div class="col-12 mt-1">
              <atom-select
                v-model="targetMultipleIpAddress.ipStatus"
                title="Status"
                placeholder="Select IP Status"
                :isAsterisk="true"
                :isClearable="false"
                :isError="isErrorAtTargetMultipleIpAddressStatus"
                :errorMessage="errorMessageAtSelector"
                :isAppendToBody="false"
                :isEnableProper="false"
                :option="ipAddressStatusOption"
              ></atom-select>
            </div>
            <div class="col-12 mt-1">
              <atom-multi-select
                v-model="targetMultipleIpAddress.tags"
                title="Tags"
                placeholder="Select Tags"
                :isAsterisk="true"
                :isPushTaggable="true"
                :isError="isErrorAtTargetMultipleIpAddressTags"
                :errorMessage="errorMessageAtSelector"
                :option="subnetAndIpTagsOption"
              ></atom-multi-select>
            </div>
            <div class="col-12 mt-1">
              <atom-datepicker
                v-model="targetMultipleIpAddress.allocationDateTime"
                title="Allocate Date"
                datepickerId="molecule-date-picker-allocate-date"
                type="dateNtime"
                placeholder="Select Start Date"
                :isClearable="true"
                class="molecule-date-picker-period-allocate-date"
              ></atom-datepicker>
            </div>
            <div class="col-12 mt-1">
              <atom-select
                v-model="targetMultipleIpAddress.idcId"
                :title="contractIdLabel"
                :placeholder="`Select ${contractIdLabel}`"
                label="idcId"
                :option="customerIdcIdList"
              ></atom-select>
            </div>
            <div v-if="targetMultipleIpAddress.idcId" class="col-12 mt-1">
              <label class="text__name-key">Organization Name: </label>
              <label class="ml-1 text__name-value">{{
                targetMultipleIpAddress.idcId.name
              }}</label>
            </div>
          </div>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- * Modal Edit Multiple Ip Address End -->

    <!-- * Modal Add New IPV6 Start -->
    <molecule-modal-dialog
      modalId="modal-add-ip-address-v6"
      buttonTextLeft="Confirm"
      buttonColorLeft="primary"
      textTitle="Add New IPV6"
      :isDisableButtonLeft="
        !isTargetIpAddressChange || isErrorOccurAtTargetIpAddress
      "
      :isSlotBody="true"
      :isCloseOnBackDrop="false"
      @clickButtonLeft="onClickConfirmAddIpAddressV6FromModal"
      @clickButtonRight="onClickCancelAddIpAddressV6FromModal"
      @clickIconClose="onClickCancelAddIpAddressV6FromModal"
    >
      <template #body>
        <div class="modal-content-container custom-overflow-y">
          <div class="row mt-2 mb-1">
            <div class="col-8">
              <atom-input
                v-model="newIpAddressV6.startIp"
                title="Start IP Address"
                placeholder="2001:db8:a0b:12f0::1"
                :isAsterisk="true"
                :isError="isErrorAtNewStartIpAddressV6"
                errorMessage="Please enter valid ipv6"
              ></atom-input>
            </div>
            <div class="col-4">
              <atom-input
                v-model="newIpAddressV6.count"
                title="Count (Number)"
                placeholder="1"
                :formatter="onlyNumberFormatter"
                :isAsterisk="true"
                :isError="isErrorAtNewIpAddressCount"
                errorMessage="Please enter count"
              ></atom-input>
            </div>
            <div class="col-12 mt-1">
              <atom-text-area
                v-model="newIpAddressV6.ipDescription"
                title="Description"
                row="3"
              ></atom-text-area>
            </div>
            <div class="col-12 mt-1">
              <atom-select
                v-model="newIpAddressV6.ipStatus"
                title="Status"
                placeholder="Select IP Status"
                :isAsterisk="true"
                :isClearable="false"
                :isError="isErrorAtNewIpAddressStatus"
                :errorMessage="errorMessageAtSelector"
                :isAppendToBody="false"
                :isEnableProper="false"
                :option="ipAddressStatusOption"
              ></atom-select>
            </div>
            <div class="col-12 mt-1">
              <atom-multi-select
                v-model="newIpAddressV6.tags"
                title="Tags"
                placeholder="Select Tags"
                :isAsterisk="true"
                :isPushTaggable="true"
                :isError="isErrorAtNewIpAddressTags"
                :errorMessage="errorMessageAtSelector"
                :option="subnetAndIpTagsOption"
              ></atom-multi-select>
            </div>
            <div class="col-12 mt-1">
              <atom-datepicker
                v-model="newIpAddressV6.allocationDateTime"
                title="Allocate Date"
                datepickerId="molecule-date-picker-allocate-date"
                type="dateNtime"
                placeholder="Select Start Date"
                :isClearable="true"
                class="molecule-date-picker-period-allocate-date"
              ></atom-datepicker>
            </div>
            <div class="col-12 mt-1">
              <atom-select
                v-model="newIpAddressV6.idcId"
                :title="contractIdLabel"
                :placeholder="`Select ${contractIdLabel}`"
                label="idcId"
                :isAppendToBody="false"
                :isEnableProper="false"
                :isClearable="true"
                :option="customerIdcIdList"
              ></atom-select>
            </div>
            <div v-if="newIpAddressV6.idcId" class="col-12 mt-1">
              <label class="text__name-key">Organization Name: </label>
              <label class="ml-1 text__name-value">{{
                newIpAddressV6.idcId.name
              }}</label>
            </div>
          </div>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- * Modal Add New IPV6 End -->

    <!-- * Modal Delete IPV6 Start -->
    <molecule-modal-dialog
      modalId="modal-confirm-delete-ip-address"
      icon="AlertCircleIcon"
      textTitle="Do you want to delete?"
      :textSubtitle="`IP Address: ${targetDeletionIp}`"
      buttonTextLeft="Delete"
      buttonTextRight="Cancel"
      buttonColorLeft="danger"
      :isTextCenter="true"
      @clickButtonLeft="onClickConfirmDeleteIpAddressFromModal"
    >
    </molecule-modal-dialog>
    <!-- * Modal Delete IPV6 End -->

    <!-- * Modal Multiple Delete IPV6 Start -->
    <molecule-modal-dialog
      modalId="modal-confirm-multiple-delete-ip-address"
      icon="AlertCircleIcon"
      textTitle="Do you want to delete?"
      textSubtitle="The Following IP Addresses"
      buttonTextLeft="Delete"
      buttonTextRight="Cancel"
      buttonColorLeft="danger"
      :isSlotBody="true"
      @clickButtonLeft="onClickConfirmDeleteMultipleIpAddress"
    >
      <template #body>
        <div class="w-100">
          <div class="modal-content-container custom-overflow-y">
            <div class="row mt-2 mb-1">
              <div class="col-12">
                <span><b>IP Address List</b></span>
                <div
                  class="preview-multiple-ip-box custom-deletion-box-height mt-1"
                >
                  <ul>
                    <li
                      v-for="(
                        ipAddress, index
                      ) in targetMultipleIpAddress.ipList"
                      :key="index"
                    >
                      {{ ipAddress.ip }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- * Modal Multiple Delete IPV6 End -->

    <!-- ? End Modal Section -->
  </div>
</template>

<script>
const replacer = (key, value) =>
  value instanceof Object && !(value instanceof Array)
    ? Object.keys(value)
        .sort()
        .reduce((sorted, key) => {
          sorted[key] = value[key]
          return sorted
        }, {})
    : value

import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomCheckbox from "@/components/atoms/AtomCheckbox.vue"
import AtomDatepicker from "@/components/atoms/AtomDatepicker.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomMultiSelect from "@/components/atoms/AtomMultiSelect.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import AtomTextArea from "@/components/atoms/AtomTextArea.vue"
import AtomSwitch from "@/components/atoms/AtomSwitch.vue"
import MoleculeSubnetCard from "@/components/molecules/portal/ipam/MoleculeSubnetCard.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import MoleculeRadioSelectData from "@/components/molecules/MoleculeRadioSelectData.vue"

import {
  BCard,
  BTable,
  BBadge,
  BPagination,
  BFormCheckbox,
  BDropdown,
  BDropdownItem,
} from "bootstrap-vue"
import { DateTime } from "luxon"
import Ripple from "vue-ripple-directive"

import AtomCollapse from "@/components/atoms/AtomCollapse.vue"
import mixins from "@/mixins"
import mixinsFormatter from "@/mixins/mixinsFormatter"
import mixinsValidator from "@/mixins/mixinsValidator"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import { IpVersionOptions } from "@/constants"
import { PermissionUser } from "@/constants/permission-user"
import mixinsUser from "@/mixins/mixinsUser"

export default {
  name: "OrganismIpamManagement",
  directives: {
    Ripple,
  },
  components: {
    AtomButton,
    AtomCheckbox,
    AtomDatepicker,
    AtomInput,
    AtomMultiSelect,
    AtomSelect,
    AtomSwitch,
    AtomTextArea,
    BFormCheckbox,
    BCard,
    BDropdown,
    BDropdownItem,
    BTable,
    BBadge,
    BPagination,
    MoleculeSubnetCard,
    MoleculeModalDialog,
    MoleculeRadioSelectData,
    AtomCollapse,
  },
  mixins: [mixins, mixinsValidator, mixinsFormatter, mixinsObjectHelper, mixinsUser],
  emits: [
    "onSelectSubnetIdx",
    "onRequestAddNewSubnet",
    "onUpdateCurrentSubnetPage",
    "onUpdateCurrentIpAddressPage",
    "onFilterSearch",
    "onRequestEditSubnet",
    "onRequestDeleteSubnet",
    "onRequestEditIpAddress",
    "onRequestEditMultipleIpAddress",
    "onRequestDeleteIpAddress",
  ],
  props: {
    subnetAddressOption: {
      type: Array,
      required: false,
      default: () => [],
    },
    subnetAddressForSidebarOption: {
      type: Array,
      required: false,
      default: () => [],
    },
    subnetAndIpTagsOption: {
      type: Array,
      required: false,
      default: () => [],
    },
    propCurrentSubnetPage: {
      type: Number,
      required: false,
      default: 1,
    },
    totalSubnetCount: {
      type: Number,
      required: false,
      default: 0,
    },
    tableIpAddressFields: {
      type: Array,
      required: false,
      default: () => [],
    },
    tableIpAddressItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    totalIpAddressCount: {
      type: Number,
      required: false,
      default: 0,
    },
    propCurrentIpAddressPage: {
      type: Number,
      required: false,
      default: 1,
    },
    forceSelectSubnetIdxToNull: {
      type: Number,
      required: false,
      default: 0,
    },
    filterList: {
      required: true,
      type: Object,
    },
    customerIdcIdList: {
      required: true,
      type: Array,
    },
    siteOptions: {
      required: true,
      type: Array,
    },
  },
  data() {
    return {
      currentSubnetPage: this.propCurrentSubnetPage,
      currentIpAddressPage: this.propCurrentIpAddressPage,
      modalId: null,
      selectedSubnet: null,
      selectedSubnetIdx: null,
      selectedSubnetIpVersion: null,
      subnetMaskOptionIpV4: Array(11)
        .fill()
        .map((_, i) => i + 22),
      subnetMaskOptionIpV6: Array(129)
        .fill()
        .map((_, i) => i),
      ipAddressStatusOption: ["Available", "Used", "Reserved"],
      newSubnetData: {
        site: null,
        subnetAddress: null,
        subnetMask: null,
        subnetDescription: null,
        subnetTags: null,
        type: null,
      },
      targetSubnet: {
        site: null,
        subnetAddress: null,
        subnetMask: null,
        subnetDescription: null,
        subnetTags: null,
        isOverrideTagsToIp: false,
      },
      targetSubnetOrigin: null,
      targetIpAddress: {
        ip: null,
        ipDescription: null,
        ipStatus: null,
        subnetId: null,
        tags: null,
        allocationDateTime: null,
        idcId: null,
      },
      targetIpAddressOrigin: null,
      targetMultipleIpAddress: {
        ipList: [],
        ipDescription: null,
        ipStatus: null,
        subnetId: null,
        tags: null,
        allocationDateTime: null,
        idcId: null,
      },
      targetMultipleIpAddressOrigin: null,
      newIpAddressV6: {
        startIp: null,
        count: 1,
        subnetId: null,
        ipDescription: null,
        ipStatus: null,
        tags: null,
        allocationDateTime: null,
        idcId: null,
      },
      newIpAddressV6Origin: null,
      filterData: {
        subnetAddress: [],
        subnetStatus: [],
        subnetTags: [],
        subnetIpAddressOrDescription: null,
        idcId: "",
        allocationDateTime: {
          start: DateTime.now().startOf("day").toISO(),
          end: DateTime.now().toISO(),
          now: DateTime.now().toISO(),
        },
      },
      isValidateNewSubnetTrigger: false,
      isValidateTargetSubnetTrigger: false,
      isValidateTargetIpAddressTrigger: false,
      isValidateTargetMultipleIpAddressTrigger: false,
      isValidateNewIpAddressV6Trigger: false,
      errorMessageAtInput: "Please enter valid data",
      errorMessageAtSelector: "Please select data",
      tableItemsPerPage: 10000,
      checkAllIpAddress: false,
      ipAddressTypeOptions: [
        {
          topic: IpVersionOptions.IPV4,
          value: 4,
        },
        {
          topic: IpVersionOptions.IPV6,
          value: 6,
        },
      ],
      targetDeletionIp: null,
    }
  },
  computed: {
    checkedIpAddressList() {
      return this.tableIpAddressItems
        .filter((ip) => ip.isChecked)
        .map((ipData) => ipData.ip)
    },
    isErrorAtNewSubnetSite() {
      return this.isValidateNewSubnetTrigger ? !this.newSubnetData.site : false
    },
    isErrorAtNewSubnetAddress() {
      if (this.newSubnetData?.type?.value === 4) {
        return this.isValidateNewSubnetTrigger
          ? this.isEmptyString(this.newSubnetData.subnetAddress) ||
              this.isInvalidIpV4(this.newSubnetData.subnetAddress)
          : false
      } else {
        return this.isValidateNewSubnetTrigger
          ? this.isEmptyString(this.newSubnetData.subnetAddress) ||
              this.isInvalidIpV6(this.newSubnetData.subnetAddress)
          : false
      }
    },
    isErrorAtNewSubnetMask() {
      return this.isValidateNewSubnetTrigger
        ? this.isNotNumeric(this.newSubnetData.subnetMask)
        : false
    },
    isErrorAtNewSubnetDescription() {
      return this.isValidateNewSubnetTrigger
        ? this.isEmptyString(this.newSubnetData.subnetDescription)
        : false
    },
    isErrorAtNewSubnetTags() {
      return this.isValidateNewSubnetTrigger
        ? this.isEmptyArrayValue(this.newSubnetData.subnetTags)
        : false
    },
    isErrorOccurAtNewSubnetData() {
      return (
        this.isErrorAtNewSubnetAddress ||
        this.isErrorAtNewSubnetMask ||
        this.isErrorAtNewSubnetDescription ||
        this.isErrorAtNewSubnetTags
      )
    },
    isErrorAtTargetSubnetSite() {
      return this.isValidateTargetSubnetTrigger
        ? !this.targetSubnet.site
        : false
    },
    isErrorAtTargetSubnetDescription() {
      return this.isValidateTargetSubnetTrigger
        ? this.isEmptyString(this.targetSubnet.subnetDescription)
        : false
    },
    isErrorAtTargetSubnetTags() {
      return this.isValidateTargetSubnetTrigger
        ? this.isEmptyArrayValue(this.targetSubnet.subnetTags)
        : false
    },
    isErrorOccurAtTargetSubnet() {
      return (
        this.isErrorAtTargetSubnetDescription || this.isErrorAtTargetSubnetTags
      )
    },
    isErrorAtTargetIpAddressStatus() {
      return this.isValidateTargetIpAddressTrigger
        ? this.isEmptyString(this.targetIpAddress.ipStatus)
        : false
    },
    isErrorAtTargetIpAddressTags() {
      return this.isValidateTargetIpAddressTrigger
        ? this.isEmptyArrayValue(this.targetIpAddress.tags)
        : false
    },
    isErrorOccurAtTargetIpAddress() {
      return (
        this.isErrorAtTargetIpAddressStatus || this.isErrorAtTargetIpAddressTags
      )
    },
    isErrorAtTargetMultipleIpAddressStatus() {
      return this.isValidateTargetMultipleIpAddressTrigger
        ? this.isEmptyString(this.targetMultipleIpAddress.ipStatus)
        : false
    },
    isErrorAtTargetMultipleIpAddressTags() {
      return this.isValidateTargetMultipleIpAddressTrigger
        ? this.isEmptyArrayValue(this.targetMultipleIpAddress.tags)
        : false
    },
    isErrorOccurAtTargetMultipleIpAddress() {
      return (
        this.isErrorAtTargetMultipleIpAddressStatus ||
        this.isErrorAtTargetMultipleIpAddressTags
      )
    },
    isTargetSubnetChange() {
      let originData = JSON.stringify(this.targetSubnetOrigin, replacer)
      let currentData = JSON.stringify(this.targetSubnet, replacer)
      return originData !== currentData
    },
    isTargetIpAddressChange() {
      let originData = JSON.stringify(this.targetIpAddressOrigin, replacer)
      let currentData = JSON.stringify(this.targetIpAddress, replacer)
      return originData !== currentData
    },
    isTargetMultipleIpAddressChange() {
      let originData = JSON.stringify(
        this.targetMultipleIpAddressOrigin,
        replacer
      )
      let currentData = JSON.stringify(this.targetMultipleIpAddress, replacer)
      return originData !== currentData
    },
    isErrorAtNewStartIpAddressV6() {
      return this.isValidateNewIpAddressV6Trigger
        ? this.isInvalidIpV6(this.newIpAddressV6.startIp)
        : false
    },
    isErrorAtNewIpAddressCount() {
      return this.isValidateNewIpAddressV6Trigger
        ? this.isNotNumeric(this.newIpAddressV6.count)
        : false
    },
    isErrorAtNewIpAddressStatus() {
      return this.isValidateNewIpAddressV6Trigger
        ? this.isEmptyString(this.newIpAddressV6.ipStatus)
        : false
    },
    isErrorAtNewIpAddressTags() {
      return this.isValidateNewIpAddressV6Trigger
        ? this.isEmptyArrayValue(this.newIpAddressV6.tags)
        : false
    },
    isErrorOccurAtNewIpAddress() {
      return (
        this.isErrorAtNewStartIpAddressV6 ||
        this.isErrorAtNewIpAddressCount ||
        this.isErrorAtNewIpAddressStatus ||
        this.isErrorAtNewIpAddressTags
      )
    },
    isNewIpAddressV6Change() {
      let originData = JSON.stringify(this.newIpAddressV6Origin, replacer)
      let currentData = JSON.stringify(this.newIpAddressV6, replacer)
      return originData !== currentData
    },
    isAllIpAddressChecked() {
      return this.tableIpAddressItems.every((ip) => ip.isChecked)
    },
    hasUpdateIpamPermission() {
      return this.hasPermission(PermissionUser.WPM.IPAM_WRITE)
    },
    hasDeleteIpamPermission() {
      return this.hasPermission(PermissionUser.WPM.IPAM_DELETE)
    },
    isShowManageIpAddressButton() {
      return (
        this.hasUpdateIpamPermission &&
        this.selectedSubnetIpVersion === 6 &&
        !this.checkedIpAddressList.length
      )
    },
  },
  watch: {
    currentSubnetPage() {
      this.$emit("onFetchingSubnetData")
    },
    currentIpAddressPage() {
      this.$emit("onFetchingIpAddressData")
    },
    forceSelectSubnetIdxToNull() {
      this.setSelectedSubnetToDefault()
    },
    filterList: {
      deep: true,
      immediate: true,
      handler() {
        this.filterData = JSON.parse(JSON.stringify(this.filterList))
      },
    },
    isAllIpAddressChecked: {
      deep: true,
      immediate: true,
      handler() {
        if (this.isAllIpAddressChecked && this.tableIpAddressItems.length) {
          this.checkAllIpAddress = true
        } else {
          this.checkAllIpAddress = false
        }
      },
    },
  },
  methods: {
    getOrganizationName(idcId) {
      const name = this.customerIdcIdList.find(
        (item) => item.id === idcId
      )?.name

      return name
    },

    badgeVariant(status) {
      switch (status.toLowerCase()) {
        case "available":
          return "light-success"
        case "used":
          return "light-danger"
        case "reserved":
          return "light-warning"
        default:
          return "light-primary"
      }
    },

    toPascalCase(words) {
      return words
        ? words.replace(/\w+/g, function (w) {
            return w[0].toUpperCase() + w.slice(1).toLowerCase()
          })
        : ""
    },

    setNewSubnetDataToDefaultValue() {
      this.newSubnetData = {
        site: null,
        subnetAddress: null,
        subnetMask: null,
        subnetDescription: null,
        subnetTags: null,
        type: null,
      }
    },

    setTargetSubnetToDefaultValue() {
      this.targetSubnet = {
        site: null,
        subnetAddress: null,
        subnetMask: null,
        subnetDescription: null,
        subnetTags: null,
        isOverrideTagsToIp: false,
      }
    },

    setTargetIpAddressToDefaultValue() {
      this.targetIpAddress = {
        ip: null,
        ipDescription: null,
        ipStatus: null,
        subnetId: null,
        tags: null,
      }
    },

    onClickButtonSearch() {
      this.$emit("onUpdateFilterData", this.filterData)
    },

    clickButtonClearFilter() {
      this.$emit("onSetDefaultFilterData")
    },

    onClickConfirmCreateNewSubnetFromModal() {
      this.isValidateNewSubnetTrigger = true
      this.$nextTick(() => {
        if (!this.isErrorOccurAtNewSubnetData) {
          const newSubnetData = JSON.parse(JSON.stringify(this.newSubnetData))
          this.$emit("onRequestAddNewSubnet", newSubnetData)
          this.isValidateNewSubnetTrigger = false
          this.closeModal()
          this.setNewSubnetDataToDefaultValue()
        }
      })
    },

    onClickCancelCreateNewSubnetFromModal() {
      this.closeModal()
      this.isValidateNewSubnetTrigger = false
      this.setNewSubnetDataToDefaultValue()
    },

    onSelectSubnetFromSidebar(targetSubnet) {
      if (this.selectedSubnetIdx !== targetSubnet.subnetId) {
        this.selectedSubnet = targetSubnet.subnetAddress
        this.selectedSubnetIdx = targetSubnet.subnetId
        this.selectedSubnetIpVersion = targetSubnet.ipVersion
        this.$emit("onSelectSubnetIdx", this.selectedSubnetIdx)
      }
    },

    onRequestEditSubnet(subnet) {
      this.targetSubnet = {
        site: subnet.site,
        subnetAddress: subnet.subnetAddress.split("/")[0],
        subnetMask: subnet.subnetAddress.split("/")[1],
        subnetDescription: subnet.subnetDescription,
        subnetTags: subnet.tags,
        isOverrideTagsToIp: false,
        ipVersion: subnet.ipVersion,
      }
      this.targetSubnetOrigin = this.deepCopyObject(this.targetSubnet)
      this.openModal("modal-edit-subnet")
    },

    onClickConfirmEditSubnetFromModal() {
      this.isValidateTargetSubnetTrigger = true
      this.$nextTick(() => {
        if (!this.isErrorOccurAtTargetSubnet) {
          const editedSubnetData = {
            siteId: this.targetSubnet.site.siteId,
            subnetDescription: this.targetSubnet.subnetDescription,
            tags: this.targetSubnet.subnetTags,
            isOverrideTagsToIp: this.targetSubnet.isOverrideTagsToIp,
          }
          this.$emit(
            "onRequestEditSubnet",
            this.selectedSubnetIdx,
            editedSubnetData
          )
          this.isValidateTargetSubnetTrigger = false
          this.closeModal()
          this.setTargetSubnetToDefaultValue()
        }
      })
    },

    onClickCancelEditSubnetFromModal() {
      this.closeModal()
      this.setTargetSubnetToDefaultValue()
    },

    onRequestDeleteSubnet(subnet) {
      this.targetSubnet = {
        site: subnet.site,
        subnetAddress: subnet.subnetAddress.split("/")[0],
        subnetMask: subnet.subnetAddress.split("/")[1],
        subnetDescription: subnet.subnetDescription,
        subnetTags: subnet.tags,
        isOverrideTagsToIp: false,
      }
      this.openModal("modal-confirm-delete-subnet")
    },

    onClickConfirmDeleteSubnetFromModal() {
      this.$emit("onRequestDeleteSubnet", this.selectedSubnetIdx)
      this.closeModal()
      this.setSelectedSubnetToDefault()
    },

    setSelectedSubnetToDefault() {
      this.selectedSubnet = null
      this.selectedSubnetIdx = null
      this.selectedSubnetIpVersion = null
    },

    onClickCancelDeleteSubnetFromModal() {
      this.closeModal()
      this.setTargetSubnetToDefaultValue()
    },

    selectRow(row) {
      if (this.hasUpdateIpamPermission) {
        this.targetIpAddress = {
          ip: row.ip.ip,
          ipDescription: row.ipDescription,
          ipStatus: this.toPascalCase(row.ip.status),
          ipVersion: row.ipVersion,
          subnetId: row.subnetId,
          tags: row.tags,
          allocationDateTime: row.allocationDateTime,
          idcId: this.customerIdcIdList.find((item) => item.id === row.idcId),
        }
        this.targetIpAddressOrigin = this.deepCopyObject(this.targetIpAddress)
        this.openModal("modal-edit-ip-address")
      }
    },

    onClickConfirmEditIpAddressFromModal() {
      this.isValidateTargetIpAddressTrigger = true
      this.$nextTick(() => {
        if (!this.isErrorOccurAtTargetIpAddress) {
          const editedIpAddressData = {
            ip: this.targetIpAddress.ip,
            ipStatus: this.targetIpAddress.ipStatus.toUpperCase(),
            ipDescription: this.targetIpAddress.ipDescription,
            tags: this.targetIpAddress.tags,
            subnetId: this.targetIpAddress.subnetId,
            allocationDateTime: this.targetIpAddress.allocationDateTime || null,
            idcId: this.targetIpAddress.idcId?.id,
          }
          this.$emit("onRequestEditIpAddress", editedIpAddressData)
          this.isValidateTargetIpAddressTrigger = false
          this.closeModal()
          this.setTargetIpAddressToDefaultValue()
        }
      })
    },

    onClickCancelEditIpAddressFromModal() {
      this.closeModal()
      this.setTargetIpAddressToDefaultValue()
    },

    toggleAll() {
      this.tableIpAddressItems.forEach((ip) => {
        ip.isChecked = this.checkAllIpAddress
      })
    },

    clickEditMultipleIpAddressFromTripleDot() {
      this.targetMultipleIpAddress = {
        ipList: this.checkedIpAddressList,
        subnetId: this.selectedSubnetIdx,
        ipDescription: null,
        ipStatus: null,
        tags: null,
        allocationDateTime: null,
        idcId: null,
      }
      this.targetMultipleIpAddressOrigin = this.deepCopyObject(
        this.targetMultipleIpAddress
      )
      this.openModal("modal-edit-multiple-ip-address")
    },

    onClickConfirmEditMultipleIpAddressFromModal() {
      this.isValidateTargetMultipleIpAddressTrigger = true
      this.$nextTick(() => {
        if (!this.isErrorOccurAtTargetMultipleIpAddress) {
          const editedMultipleIpAddressData = {
            ipAddresses: this.targetMultipleIpAddress.ipList.map(
              (ipData) => ipData.ip
            ),
            subnetId: this.targetMultipleIpAddress.subnetId,
            ipStatus: this.targetMultipleIpAddress.ipStatus.toUpperCase(),
            ipDescription: this.targetMultipleIpAddress.ipDescription,
            tags: this.targetMultipleIpAddress.tags,
            idcId: this.targetMultipleIpAddress.idcId,
            allocationDateTime:
              this.targetMultipleIpAddress.allocationDateTime || null,
          }
          this.$emit(
            "onRequestEditMultipleIpAddress",
            editedMultipleIpAddressData
          )
          this.isValidateTargetMultipleIpAddressTrigger = false
          this.closeModal()
          this.setTargetMultipleIpAddressToDefaultValue()
        }
      })
    },

    onClickCancelEditMultipleIpAddressFromModal() {
      this.closeModal()
      this.setTargetMultipleIpAddressToDefaultValue()
    },

    setTargetMultipleIpAddressToDefaultValue() {
      this.targetMultipleIpAddress = {
        ipList: [],
        subnetId: null,
        ipDescription: null,
        ipStatus: null,
        tags: null,
        allocationDateTime: null,
        idcId: null,
      }
    },

    clickAddNewIpV6() {
      this.newIpAddressV6.subnetId = this.selectedSubnetIdx
      this.newIpAddressV6Origin = this.deepCopyObject(this.newIpAddressV6)
      this.openModal("modal-add-ip-address-v6")
    },

    onClickConfirmAddIpAddressV6FromModal() {
      this.isValidateNewIpAddressV6Trigger = true
      this.$nextTick(() => {
        if (!this.isErrorOccurAtNewIpAddress) {
          const newIpAddressData = {
            ip: this.newIpAddressV6.startIp,
            rangeIp: this.newIpAddressV6.count,
            subnetId: this.newIpAddressV6.subnetId,
            ipStatus: this.newIpAddressV6.ipStatus.toUpperCase(),
            ipDescription: this.newIpAddressV6.ipDescription,
            tags: this.newIpAddressV6.tags,
            idcId: this.newIpAddressV6.idcId?.id,
            allocationDateTime: this.newIpAddressV6.allocationDateTime || null,
            ipVersion: 6,
          }
          this.$emit("onRequestAddNewIpAddressV6", newIpAddressData)
          this.isValidateNewIpAddressV6Trigger = false
          this.closeModal()
          this.setTargetNewIpAddressV6ToDefaultValue()
        }
      })
    },

    onClickCancelAddIpAddressV6FromModal() {
      this.closeModal()
      this.setTargetNewIpAddressV6ToDefaultValue()
    },

    setTargetNewIpAddressV6ToDefaultValue() {
      this.newIpAddressV6 = {
        startIp: null,
        count: 1,
        subnetId: null,
        ipDescription: null,
        ipStatus: null,
        tags: null,
        allocationDateTime: null,
        idcId: null,
      }
    },

    clickDeleteSingleIp(targetIp) {
      this.targetDeletionIp = targetIp.ip.ip
      this.openModal("modal-confirm-delete-ip-address")
    },

    onClickConfirmDeleteIpAddressFromModal() {
      this.closeModal()
      const targetIpData = {
        subnetId: this.selectedSubnetIdx,
        ipAddress: this.targetDeletionIp,
      }
      this.$emit("onRequestDeleteIpAddress", targetIpData)
    },

    clickDeleteMultipleIpAddressFromTripleDot() {
      this.targetMultipleIpAddress = {
        ipList: this.checkedIpAddressList,
        subnetId: this.selectedSubnetIdx,
        ipDescription: null,
        ipStatus: null,
        tags: null,
        allocationDateTime: null,
        idcId: null,
      }
      this.openModal("modal-confirm-multiple-delete-ip-address")
    },

    onClickConfirmDeleteMultipleIpAddress() {
      this.closeModal()
      const targetMultipleIpData = {
        subnetId: this.selectedSubnetIdx,
        ipAddresses: this.checkedIpAddressList.map((item) => item.ip),
      }
      this.$emit("onRequestDeleteMultipleIpAddresses", targetMultipleIpData)
    },

    // modal
    openModal(id) {
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },
    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
    },
  },
}
</script>

<style lang="scss" scoped>
.ipam-management-card::v-deep .b-table-sticky-header {
  max-height: none;
}

hr {
  margin-top: 1.5rem;
  margin-bottom: 0;
  border: 0;
  border-top: 1px solid #ebe9f1;
}

ul {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

ul > li {
  line-height: 25px;
  margin: 0;
}

.edit__dropdown {
  margin-left: 15px;
}

.ipam-management-card {
  background: white;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.06);
  border-radius: 6px;
}

.card__header--topic {
  font-weight: 500;
  font-size: 18px;
}

.card-padding {
  padding: 1.5rem;
}

.card-padding-x {
  padding-left: 21px;
  padding-right: 21px;
}

.card-padding-y {
  padding-top: 21px;
  padding-bottom: 21px;
}

.card-padding-top {
  padding-top: 21px;
}

.card-padding-bottom {
  padding-bottom: 21px;
}

.custom-button-width-90 {
  width: 90%;
}

.custom-position-sticky {
  position: sticky;
  top: 90px;
}

.subnet-sidebar {
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.06);
}

.modal-content-container {
  padding-left: 16px;
  padding-right: 16px;
}

.custom-overflow-y {
  max-height: 400px;
  overflow-y: auto;
}

.custom-cell-checkbox {
  height: 25px;
  width: 25px;
  white-space: nowrap;
}

.custom-cell-subnet-ip-address {
  width: 100%;
  white-space: nowrap;
}

.custom-cell-subnet-ip-status {
  width: 70px;
  white-space: nowrap;
}

.custom-cell-subnet-tags {
  min-width: 100px;
  max-width: fit-content;
  white-space: nowrap;
}

.custom-cell-subnet-description {
  width: 100%;
  white-space: nowrap;
}

.custom-cell-allocate-date {
  width: 100%;
  white-space: nowrap;
}

.custom-cell-subnet-idc-id {
  color: #6e6b7b;
  font-size: 14px;
  font-weight: 500;
  width: 100%;
  white-space: nowrap;
}

.subnet-card--custom-max-height {
  max-height: 300px;
  overflow: auto;
}

.badge--custom-margin-right {
  margin-right: 6px;
}

.checkbox--custom-margin-top {
  margin-top: 12px;
}

.custom-margin-top {
  margin-top: 5px;
}

.custom-padding-bottom {
  padding-bottom: 3px;
}

.sub-header {
  font-weight: 400;
  font-size: 12px;
  color: #b9b9c3;
}

.sub-header-2 {
  color: #6e6b7b;
  font-size: 12px;
  font-weight: 400;
}

.custom-multiple-ip-list-box {
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  max-height: 120px;
  overflow-y: auto;
}

.preview-multiple-ip-box {
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
}

.custom-edition-box-height {
  max-height: 120px;
  overflow-y: auto;
}

.custom-deletion-box-height {
  max-height: 300px;
  overflow-y: auto;
}

.text__title {
  color: #5e5873;
  font-size: 12px;
  margin-bottom: 5px;
  line-height: 1.5;
}

.text__asterisk {
  color: #ea5455;
  font-size: 12px;
  margin-bottom: 0;
}

.filter__switch {
  padding-top: 10px;
  height: fit-content;
}

.reset-filter-button {
  color: #b9b9c3;
  margin-bottom: 8px;
  text-decoration: underline;
  &:hover {
    cursor: pointer;
    color: #7367f0;
  }
}

.text__name-key,
.text__name-value {
  color: #5e5873;
  font-size: 12px;
}

.text__name-value {
  font-weight: 500;
  font-size: 14px;
}

.ip-header-sticky {
  background-color: white;
  border-bottom: 1px solid #ebe9f1;
  z-index: 10;
  position: sticky;
  top: 80px;
  margin: 0;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .custom-overflow-y {
    max-height: 500px;
    overflow-y: auto;
  }
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
  .subnet-sidebar {
    min-height: 100%;
  }

  .modal-content-container {
    padding-left: 52px;
    padding-right: 52px;
  }

  .custom-max-height {
    max-height: 100%;
  }
}
</style>
